import React from 'react';
import { CheckCircle, ChevronRight, ArrowRight, Rocket } from 'lucide-react';
import { Link } from 'react-router-dom';
import MPHARMA from '../assets/schools/pharma/M pharma.jpg';

const HowToApplySection = ({ applicationImage }) => {
  const CTASection = () => (
    <div className="relative overflow-hidden bg-gradient-to-r from-emerald-800 to-blue-900 text-white py-12 px-8 rounded-xl mb-12">
      <div className="absolute inset-0 bg-cover bg-center opacity-10 mix-blend-overlay"></div>
      <div className="absolute -right-20 -bottom-20 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>

      <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-8">
        <div>
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Shape Your Digital Future?</h2>
          <p className="text-white/80 max-w-xl">Join this School at Maya Devi University and become part of a community dedicated to innovation, excellence, and responsible technology leadership.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="https://admission.maya.edu.in/">
            <button
              className="bg-white text-emerald-800 px-8 py-4 rounded-lg text-lg font-semibold flex items-center gap-2 shadow-lg whitespace-nowrap hover:bg-emerald-50 transition-all duration-300"
            >
              Apply Now
              <Rocket className="w-5 h-5" />
            </button>
          </Link>
        </div>
      </div>
    </div>
  );

  // Application steps with detailed information
  const applicationSteps = [
    {
      title: "Check Eligibility",
      description: "Ensure you meet the eligibility criteria: 10+2 with Physics, Chemistry, and Mathematics/Biology with minimum required percentage."
    },
    {
      title: "Complete Online Application",
      description: "Fill out the online application form at Maya Devi University's admission portal with accurate personal and academic details."
    },
    {
      title: "Document Submission",
      description: "Upload required documents including 10+2 marksheet, transfer certificate, character certificate, and passport-sized photographs."
    },
    {
      title: "Application Fee Payment",
      description: "Pay the non-refundable application fee through online payment modes (credit/debit card, net banking, UPI)."
    },
    {
      title: "Admission Confirmation",
      description: "Upon selection, confirm your seat by paying the initial fee within the specified timeline to secure your admission."
    }
  ];

  return (
    <div className="bg-gradient-to-b from-white to-emerald-50 py-20 m-16">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center text-emerald-800 mb-4">
          How to Apply
        </h2>
        <p className="text-center text-emerald-600 mb-16 max-w-2xl mx-auto">
          Start your journey towards becoming a pharmaceutical professional with our simple application process
        </p>

        {/* Main content area with responsive flex direction */}
        <div className="flex flex-col-reverse lg:flex-row gap-8 mb-16">
          {/* Application Steps - Now on the left on desktop */}
          <div className="w-full lg:w-1/2">
            <div className="bg-white rounded-xl shadow-xl overflow-hidden border border-emerald-100 h-full">
              <div className="bg-emerald-700 py-4 px-6">
                <h3 className="text-xl font-bold text-white">Application Process</h3>
              </div>
              <div className="p-6">
                <div className="space-y-6">
                  {applicationSteps.map((step, index) => (
                    <div key={index} className="relative pl-10 pb-6">
                      {/* Vertical line connecting steps */}
                      {index < applicationSteps.length - 1 && (
                        <div className="absolute left-4 top-8 bottom-0 w-0.5 bg-emerald-200 z-0"></div>
                      )}

                      {/* Step circle */}
                      <div className="absolute left-0 top-0 bg-emerald-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm z-10">
                        {index + 1}
                      </div>

                      <div className="bg-emerald-50 p-4 rounded-lg shadow-sm hover:shadow-md transition">
                        <h4 className="font-bold text-emerald-800 text-lg">
                          {step.title}
                        </h4>
                        <p className="text-emerald-600 mt-1">
                          {step.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Images Section - Now on the right on desktop */}
          <div className="w-full lg:w-1/2 flex flex-col gap-6">
            {/* Main promotional image */}
            <div className="relative mb-6">
              <img
                src={applicationImage || "/api/placeholder/600/400"}
                alt="Application Process"
                className="rounded-xl shadow-xl w-full h-64 md:h-80 object-cover"
              />
              <div className="absolute -bottom-4 -right-4 w-full h-full bg-emerald-200 rounded-xl -z-10"></div>
            </div>

            {/* M Pharma image */}
            <div className="bg-white rounded-xl shadow-lg p-4">
              <img
                src={MPHARMA}
                alt="M Pharma"
                className="w-full h-64 md:h-80 object-cover rounded-lg"
              />
            </div>

            {/* CTA button */}
            <a
              href="https://admission.maya.edu.in/"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 rounded-full 
                        shadow-lg hover:shadow-xl transition duration-300 flex items-center justify-center group whitespace-nowrap mt-2"
            >
              Apply Now
              <ChevronRight
                className="ml-2 group-hover:translate-x-1 transition"
                size={20}
              />
            </a>
          </div>
        </div>

        {/* CTA Section at the bottom */}
        <CTASection />
      </div>
    </div>
  );
};

export default HowToApplySection;